<template>
  <article class="invoice-create-card invoice-create-card--accent-billing">
    <header class="invoice-create-card__head invoice-create-card__head--receiver invoice-create-card__head--space">
      <h3>
        <i class="fa fa-user-o invoice-create-section-icon invoice-create-section-icon--billing" aria-hidden="true"></i>
        {{ text.create.ui.receiverCard.title }}
      </h3>

      <div
        v-if="!readOnly && !receiverLocked && allowInlineCreate"
        ref="inlinePopoverWrap"
        class="invoice-create-inline-popover-wrap"
      >
        <button type="button" class="expense-inline-link" @click.stop="toggleInlineForm">
          {{ toggleInlineFormLabel }}
        </button>

        <div
          v-if="showInlineForm"
          class="invoice-create-inline-popover app-modal-inline-create"
          @click.stop
        >
          <div class="invoice-create-inline-popover__grid">
            <label v-if="!inlineBillingDataOnly" class="invoice-create-field invoice-create-field--full app-modal-field">
              <span>{{ text.create.ui.receiverCard.fields.customerRequired }}</span>
              <input v-model.trim="inlineCustomer.clientName" type="text" :placeholder="text.create.ui.receiverCard.placeholders.clientName" />
            </label>

            <label class="invoice-create-field app-modal-field">
              <span>{{ text.create.ui.receiverCard.fields.rfcRequired }}</span>
              <input v-model.trim="inlineCustomer.rfc" type="text" :placeholder="billingDataText.modal.form.fiscalIdentification.placeholders.taxIdentifier" />
            </label>

            <label class="invoice-create-field app-modal-field">
              <span>{{ text.create.ui.receiverCard.fields.businessNameRequired }}</span>
              <input v-model.trim="inlineCustomer.businessName" type="text" :placeholder="billingDataText.modal.form.fiscalIdentification.placeholders.fiscalName" />
            </label>

            <div class="invoice-create-field invoice-create-field--full app-modal-field">
              <span>{{ text.create.ui.receiverCard.fields.taxRegimeRequired }}</span>
              <multiselect
                v-model="inlineCustomer.taxRegimeOption"
                :options="effectiveTaxRegimeOptions"
                track-by="value"
                label="label"
                :show-labels="false"
                :searchable="true"
                :allow-empty="false"
                class="app-modal-select invoice-create-inline-select"
                :placeholder="billingDataText.modal.form.fiscalConfiguration.placeholders.fiscalRegime"
              />
            </div>

            <div class="invoice-create-field app-modal-field">
              <span>{{ text.create.ui.receiverCard.fields.cfdiUseRequired }}</span>
              <multiselect
                v-model="inlineCustomer.cfdiUseOption"
                :options="effectiveCfdiUseOptions"
                track-by="value"
                label="label"
                :show-labels="false"
                :searchable="true"
                :allow-empty="false"
                class="app-modal-select invoice-create-inline-select"
                :placeholder="billingDataText.modal.form.fiscalConfiguration.placeholders.cfdiUse"
              />
            </div>

            <label class="invoice-create-field app-modal-field">
              <span>{{ text.create.ui.receiverCard.fields.fiscalResidenceRequired }}</span>
              <input v-model.trim="inlineCustomer.fiscalResidence" type="text" :placeholder="billingDataText.modal.form.fiscalIdentification.placeholders.fiscalZipCode" />
            </label>
          </div>

          <div class="app-modal-inline-create__actions invoice-create-inline-actions">
            <button type="button" class="btn btn-light" @click="cancelInlineForm">{{ generalText.common.cancel }}</button>
            <button
              type="button"
              class="app-header-btn app-header-btn--primary"
              :disabled="!canCreateInlineCustomer"
              @click="emitCreateInlineCustomer"
            >
              {{ saveInlineButtonLabel }}
            </button>
          </div>
        </div>
      </div>
    </header>

    <multiselect
      v-if="showReceiverSelector"
      v-model="selectedCustomerModel"
      :options="effectiveCustomerOptions"
      track-by="value"
      label="label"
      :custom-label="customerSearchLabel"
      :show-labels="false"
      :searchable="true"
      :allow-empty="!receiverLocked"
      :placeholder="receiverLocked ? text.create.ui.receiverCard.placeholders.selectorLocked : text.create.ui.receiverCard.placeholders.selectorSearch"
      class="invoice-create-receiver-select app-modal-select"
    >
      <template #option="{ option }">
        <div class="expense-supplier-option">
          <small><strong>{{ billingDataText.modal.taxIdentifierLabels.national }}:</strong> {{ option?.meta?.rfc || '-' }}</small>
          <small><strong>{{ billingDataText.modal.form.fiscalIdentification.fields.fiscalName }}:</strong> {{ option?.meta?.businessName || '-' }}</small>
          <small><strong>{{ text.create.ui.receiverCard.fields.customer }}:</strong> {{ option?.meta?.clientName || '-' }}</small>
        </div>
      </template>
      <template #singleLabel="{ option }">
        <div class="expense-supplier-option invoice-create-receiver-single">
          <span>{{ option?.meta?.businessName || option?.label || '' }}</span>
        </div>
      </template>
      <template #noResult>
        <span>{{ generalText.common.noOption }}</span>
      </template>
      <template #noOptions>
        <span>{{ generalText.common.noOption }}</span>
      </template>
    </multiselect>

    <div class="invoice-create-receiver-preview">
      <template v-if="selectedReceiver">
        <button
          v-if="!readOnly"
          type="button"
          class="app-table-icon-btn app-table-icon-btn--edit invoice-create-receiver-preview-edit-btn"
          :title="billingDataText.card.actions.edit"
          @click="openContributorModal"
        >
          <i class="fa fa-pencil" aria-hidden="true"></i>
        </button>

        <div class="invoice-create-receiver-preview-grid">
          <div class="invoice-create-receiver-kv">
            <span>{{ text.create.ui.receiverCard.fields.customer }}</span>
            <strong>{{ selectedReceiver.meta?.clientName || selectedReceiver.clientName || '-' }}</strong>
          </div>

          <div class="invoice-create-receiver-kv">
            <span>{{ billingDataText.modal.taxIdentifierLabels.national }}</span>
            <strong>{{ selectedReceiver.meta?.rfc || selectedReceiver.rfc || '-' }}</strong>
          </div>

          <div class="invoice-create-receiver-kv">
            <span>{{ billingDataText.modal.form.fiscalIdentification.fields.fiscalName }}</span>
            <strong>{{ selectedReceiver.meta?.businessName || selectedReceiver.businessName || '-' }}</strong>
          </div>

          <div class="invoice-create-receiver-kv">
            <span>{{ text.create.ui.receiverCard.fields.taxRegime }}</span>
            <strong>{{ selectedReceiver.meta?.taxRegime || selectedReceiver.taxRegime || '-' }}</strong>
          </div>

          <div class="invoice-create-receiver-kv">
            <span>{{ billingDataText.modal.form.fiscalConfiguration.fields.cfdiUse }}</span>
            <strong>{{ selectedReceiver.meta?.cfdiUse || selectedReceiver.cfdiUse || '-' }}</strong>
          </div>

          <div class="invoice-create-receiver-kv">
            <span>{{ billingDataText.modal.form.fiscalIdentification.fields.fiscalZipCode }}</span>
            <strong>{{ selectedReceiver.meta?.fiscalResidence || selectedReceiver.fiscalResidence || '-' }}</strong>
          </div>
        </div>
      </template>
      <span v-else>{{ text.create.ui.receiverCard.emptyState }}</span>
    </div>

    <ContributorModal
      v-if="!readOnly"
      :show="showContributorModal"
      :mode="contributorModalMode"
      :initial-data="contributorModalInitialData"
      :state-options="[]"
      :regimen-options="effectiveTaxRegimeOptions"
      :uso-cfdi-options="effectiveCfdiUseOptions"
      :payment-method-options="effectivePaymentFormOptions"
      :payment-method-type-options="effectivePaymentMethodOptions"
      :loading="contributorModalLoading"
      @close="closeContributorModal"
      @submit="onContributorModalSubmit"
    />
  </article>
</template>

<script>
import ContributorModal from '@/components/contributors/ContributorModal.vue';
import invoicesService from '@/services/invoices';
import customersService from '@/services/customers';
import { useDomainText } from '@/composables/text/useDomainText';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';

const { catalog: billingDataText } = useDomainText('billingData');
const { catalog: invoiceText } = useDomainText('invoices');
const { catalog: generalText } = useDomainText('general');

export default {
  name: 'InvoiceCreateReceiverCard',
  components: {
    ContributorModal,
  },
  props: {
    customerOptions: {
      type: Array,
      default: () => [],
    },
    selectedReceiver: {
      type: Object,
      default: null,
    },
    taxRegimeOptions: {
      type: Array,
      default: () => [],
    },
    cfdiUseOptions: {
      type: Array,
      default: () => [],
    },
    paymentMethodOptions: {
      type: Array,
      default: () => [],
    },
    paymentFormOptions: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    receiverLocked: {
      type: Boolean,
      default: false,
    },
    inlineBillingDataOnly: {
      type: Boolean,
      default: false,
    },
    inlineCustomerId: {
      type: Number,
      default: 0,
    },
    allowInlineCreate: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['select-receiver', 'create-inline-customer'],
  data() {
    return {
      text: invoiceText,
      billingDataText,
      generalText,
      showInlineForm: false,
      showContributorModal: false,
      contributorModalLoading: false,
      contributorModalMode: 'edit',
      contributorModalInitialData: null,
      inlineCustomer: {
        clientName: '',
        rfc: '',
        businessName: '',
        taxRegimeOption: null,
        cfdiUseOption: null,
        fiscalResidence: '',
      },
    };
  },
  computed: {
    effectiveTaxRegimeOptions() {
      return Array.isArray(this.taxRegimeOptions) && this.taxRegimeOptions.length > 0
        ? this.taxRegimeOptions
        : [];
    },
    effectiveCfdiUseOptions() {
      return Array.isArray(this.cfdiUseOptions) && this.cfdiUseOptions.length > 0
        ? this.cfdiUseOptions
        : [];
    },
    effectivePaymentMethodOptions() {
      return Array.isArray(this.paymentMethodOptions) ? this.paymentMethodOptions : [];
    },
    effectivePaymentFormOptions() {
      return Array.isArray(this.paymentFormOptions) ? this.paymentFormOptions : [];
    },
    selectedContributorId() {
      return Number(this.selectedReceiver?.meta?.id || this.selectedReceiver?.value || this.selectedReceiver?.id || 0) || 0;
    },
    selectedCustomerId() {
      return Number(
        this.selectedReceiver?.meta?.customerId
        || this.selectedReceiver?.customerId
        || this.selectedReceiver?.meta?.companyId
        || this.selectedReceiver?.companyId
        || 0
      ) || 0;
    },
    selectedCustomerModel: {
      get() {
        return this.selectedReceiver;
      },
      set(value) {
        this.$emit('select-receiver', value || null);
      },
    },
    effectiveCustomerOptions() {
      const options = Array.isArray(this.customerOptions) ? this.customerOptions : [];

      if (this.inlineBillingDataOnly) {
        const inlineCustomerId = Number(this.inlineCustomerId || 0) || 0;
        if (inlineCustomerId <= 0) {
          return [];
        }

        return options.filter((option) => {
          const optionCustomerId = Number(
            option?.customerId
            || option?.meta?.customerId
            || option?.meta?.companyId
            || option?.companyId
            || 0
          ) || 0;

          return optionCustomerId === inlineCustomerId;
        });
      }

      if (!this.receiverLocked) {
        return options;
      }

      const lockedCustomerId = this.selectedCustomerId;
      if (lockedCustomerId <= 0) {
        return this.selectedReceiver ? [this.selectedReceiver] : [];
      }

      const filtered = options.filter((option) => {
        const optionCustomerId = Number(
          option?.customerId
          || option?.meta?.customerId
          || option?.meta?.companyId
          || option?.companyId
          || 0
        ) || 0;

        return optionCustomerId === lockedCustomerId;
      });

      if (filtered.length > 0) {
        return filtered;
      }

      return this.selectedReceiver ? [this.selectedReceiver] : [];
    },
    showReceiverSelector() {
      if (this.readOnly) {
        return false;
      }

      if (this.inlineBillingDataOnly && this.effectiveCustomerOptions.length === 0) {
        return false;
      }

      if (!this.receiverLocked) {
        return true;
      }

      return this.effectiveCustomerOptions.length > 1;
    },
    toggleInlineFormLabel() {
      if (this.showInlineForm) {
        return this.text.create.ui.receiverCard.toggle.hide;
      }

      if (this.inlineBillingDataOnly) {
        return this.text.create.ui.receiverCard.toggle.showNew || '+ Nuevo';
      }

      return this.text.create.ui.receiverCard.toggle.showNewCustomer;
    },
    saveInlineButtonLabel() {
      if (this.inlineBillingDataOnly) {
        return this.text.create.ui.receiverCard.actions.saveAndUse || 'Guardar y usar';
      }

      return this.text.create.ui.receiverCard.actions.saveAndUseCustomer;
    },
    canCreateInlineCustomer() {
      const hasClient = this.inlineBillingDataOnly || this.inlineCustomer.clientName !== '';

      return hasClient
        && this.inlineCustomer.rfc !== ''
        && this.inlineCustomer.businessName !== ''
        && this.inlineCustomer.taxRegimeOption !== null
        && this.inlineCustomer.cfdiUseOption !== null
        && this.inlineCustomer.fiscalResidence !== '';
    },
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  },
  methods: {
    handleDocumentClick(event) {
      if (!this.showInlineForm) {
        return;
      }

      const wrap = this.$refs.inlinePopoverWrap;
      if (!wrap || typeof wrap.contains !== 'function') {
        return;
      }

      if (!wrap.contains(event?.target)) {
        this.cancelInlineForm();
      }
    },
    customerSearchLabel(option) {
      const rfc = String(option?.meta?.rfc || option?.rfc || '').trim();
      const businessName = String(option?.meta?.businessName || option?.businessName || option?.label || '').trim();
      const clientName = String(option?.meta?.clientName || option?.clientName || '').trim();

      return [businessName, rfc, clientName].filter((item) => item !== '').join(' | ');
    },
    mapReceiverSelectorOption(item) {
      const row = item && typeof item === 'object' ? item : {};

      return {
        value: Number(row?.contributor_id || row?.id || 0),
        label: String(row?.business_name || row?.name || row?.client_name || '').trim(),
        customerId: Number(row?.customer_id || row?.company_id || 0),
        meta: {
          id: Number(row?.contributor_id || row?.id || 0),
          customerId: Number(row?.customer_id || row?.company_id || 0),
          clientName: String(row?.client_name || '').trim(),
          rfc: String(row?.rfc || '').trim(),
          businessName: String(row?.business_name || row?.name || '').trim(),
          cfdiUseCode: String(row?.cfdi_use || '').trim(),
          cfdiUse: String(row?.cfdi_use_label || row?.cfdi_use || '').trim(),
          taxRegimeCode: String(row?.tax_regime || '').trim(),
          taxRegime: String(row?.tax_regime_label || row?.tax_regime || '').trim(),
          fiscalResidence: String(row?.fiscal_residence || row?.zipcode || '').trim(),
        },
      };
    },
    async openContributorModal() {
      if (this.selectedContributorId <= 0) {
        this.$toast?.error(this.text.create.receiver.validation.receiverRequiredToEdit);
        return;
      }

      this.contributorModalLoading = true;
      try {
        const response = await invoicesService.contributorShow(this.selectedContributorId);
        const payload = extractPayload(response);

        this.contributorModalInitialData = payload && typeof payload === 'object' ? { ...payload } : null;
        this.contributorModalMode = 'edit';
        this.showContributorModal = true;
      } catch (error) {
        const message = error?.response?.data?.message
          || this.text.create.receiver.toast.loadContributorError
          || this.billingDataText.toast.saveError;
        this.$toast?.error(message);
      } finally {
        this.contributorModalLoading = false;
      }
    },
    closeContributorModal() {
      this.showContributorModal = false;
      this.contributorModalLoading = false;
      this.contributorModalMode = 'edit';
      this.contributorModalInitialData = null;
    },
    async onContributorModalSubmit(payload) {
      if (this.selectedCustomerId <= 0 || this.selectedContributorId <= 0) {
        this.$toast?.error(this.text.create.receiver.validation.customerRequiredToUpdate);
        return;
      }

      this.contributorModalLoading = true;
      try {
        await customersService.contributorsUpdate(this.selectedCustomerId, this.selectedContributorId, payload || {});

        const selectorsParams = this.receiverLocked && this.selectedCustomerId > 0
          ? { customer_id: this.selectedCustomerId }
          : {};
        const selectorsResponse = await invoicesService.receiverSelectors(selectorsParams);
        const selectors = extractCollection(selectorsResponse)
          .map((item) => this.mapReceiverSelectorOption(item))
          .filter((option) => {
            if (Number(option?.value || 0) <= 0) {
              return false;
            }

            if (!this.receiverLocked) {
              return true;
            }

            const optionCustomerId = Number(option?.customerId || option?.meta?.customerId || 0) || 0;
            return optionCustomerId === this.selectedCustomerId;
          });

        const updatedReceiver = selectors.find((option) => Number(option?.value || 0) === this.selectedContributorId) || null;
        if (updatedReceiver) {
          this.$emit('select-receiver', updatedReceiver);
        }

        this.$toast?.success(this.billingDataText.toast.updateSuccess);
        this.closeContributorModal();
      } catch (error) {
        const message = error?.response?.data?.message
          || this.billingDataText.toast.saveError;
        this.$toast?.error(message);
        this.contributorModalLoading = false;
      }
    },
    toggleInlineForm() {
      this.showInlineForm = !this.showInlineForm;
      if (!this.showInlineForm) {
        this.resetInlineCustomer();
      }
    },
    cancelInlineForm() {
      this.showInlineForm = false;
      this.resetInlineCustomer();
    },
    emitCreateInlineCustomer() {
      if (!this.canCreateInlineCustomer) {
        return;
      }

      this.$emit('create-inline-customer', {
        customerId: Number(this.inlineCustomerId || 0) || 0,
        clientName: this.inlineCustomer.clientName,
        rfc: this.inlineCustomer.rfc,
        businessName: this.inlineCustomer.businessName,
        taxRegimeCode: this.inlineCustomer.taxRegimeOption?.value || '',
        taxRegimeLabel: this.inlineCustomer.taxRegimeOption?.label || '',
        cfdiUseCode: this.inlineCustomer.cfdiUseOption?.value || '',
        cfdiUseLabel: this.inlineCustomer.cfdiUseOption?.label || '',
        fiscalResidence: this.inlineCustomer.fiscalResidence,
      });

      this.cancelInlineForm();
    },
    resetInlineCustomer() {
      this.inlineCustomer = {
        clientName: '',
        rfc: '',
        businessName: '',
        taxRegimeOption: null,
        cfdiUseOption: null,
        fiscalResidence: '',
      };
    },
  },
};
</script>
